import { useState, lazy, Suspense } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Loader2, Mail, Lock, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/AuthContext'

// Escena 3D (Three.js) del panel derecho: se carga bajo demanda para no
// pesar en el bundle principal. Mientras carga se ve el degradado magenta.
const SlotScene = lazy(() => import('@/components/SlotScene'))

export default function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [remember, setRemember] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    const res = await login(email, password)
    setSubmitting(false)
    if (res.success) navigate(res.redirectTo, { replace: true })
  }

  return (
    <div className="min-h-screen flex bg-white">
      {/* ============================== */}
      {/* IZQUIERDA — FORMULARIO         */}
      {/* ============================== */}
      <div className="relative w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 lg:p-16 bg-white overflow-hidden">

        {/* Acentos decorativos sutiles */}
        <div
          className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none opacity-60"
          style={{
            background:
              'radial-gradient(circle at center, rgba(184,26,151,0.10) 0%, transparent 65%)',
          }}
        />
        <div
          className="absolute -bottom-40 -right-20 w-[360px] h-[360px] rounded-full pointer-events-none opacity-50"
          style={{
            background:
              'radial-gradient(circle at center, rgba(212,86,187,0.08) 0%, transparent 60%)',
          }}
        />
        {/* Línea acento lateral */}
        <div
          className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-[3px] rounded-full"
          style={{
            background:
              'linear-gradient(180deg, transparent 0%, #B81A97 50%, transparent 100%)',
          }}
        />

        {/* Contenido del formulario */}
        <div className="relative w-full max-w-md animate-[fadeUp_0.6s_ease-out]">

          {/* Logo + título */}
          <div className="mb-10 flex items-center gap-4">
            <div className="relative">
              <div
                className="absolute -inset-2 rounded-2xl opacity-40 blur-xl"
                style={{
                  background:
                    'linear-gradient(135deg, #B81A97 0%, #D456BB 100%)',
                }}
              />
              <img
                src="/logoTr.svg"
                alt="Control Salas"
                className="relative h-14 w-14 rounded-2xl object-contain shadow-lg ring-1 ring-slate-200 bg-white"
              />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#B81A97] font-semibold">
                Innomatic
              </p>
              <h1 className="text-xl font-bold text-slate-900 leading-tight">
                Control Salas
              </h1>
            </div>
          </div>

          {/* Bienvenida */}
          <div className="mb-8">
            <h2 className="text-[2.25rem] leading-[1.15] font-bold text-slate-900 mb-3 tracking-tight">
              Bienvenido de{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#B81A97]">nuevo</span>
                <span
                  className="absolute left-0 right-0 bottom-1 h-2 -z-0 rounded-full"
                  style={{
                    background:
                      'linear-gradient(90deg, rgba(184,26,151,0.15) 0%, rgba(212,86,187,0.20) 100%)',
                  }}
                />
              </span>
              .
            </h2>
            <p className="text-slate-500 text-[15px] leading-relaxed">
              Introduce tus credenciales para acceder al panel.
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* EMAIL */}
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-xs font-semibold text-slate-700 uppercase tracking-wider"
              >
                Email
              </Label>
              <div className="group relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#B81A97] transition-colors pointer-events-none">
                  <Mail className="h-[18px] w-[18px]" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  autoComplete="email"
                  required
                  className="flex w-full h-12 pl-11 pr-4 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 transition-all focus:outline-none focus:ring-4 focus:ring-[#B81A97]/15 focus:border-[#B81A97] focus:bg-white hover:border-slate-300"
                />
              </div>
            </div>

            {/* PASSWORD */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="password"
                  className="text-xs font-semibold text-slate-700 uppercase tracking-wider"
                >
                  Contraseña
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-[#B81A97] hover:text-[#9F1683] transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="group relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#B81A97] transition-colors pointer-events-none">
                  <Lock className="h-[18px] w-[18px]" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="flex w-full h-12 pl-11 pr-12 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 transition-all focus:outline-none focus:ring-4 focus:ring-[#B81A97]/15 focus:border-[#B81A97] focus:bg-white hover:border-slate-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-[#B81A97] hover:bg-[#B81A97]/5 transition"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Recordar sesión */}
            <label className="flex items-center gap-2.5 cursor-pointer select-none group">
              <span className="relative inline-flex h-[18px] w-[18px] items-center justify-center">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="peer sr-only"
                />
                <span
                  className="h-[18px] w-[18px] rounded-md border-2 border-slate-300 bg-white transition-all peer-checked:bg-[#B81A97] peer-checked:border-[#B81A97] group-hover:border-[#B81A97]/60"
                />
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="pointer-events-none absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">
                Mantener sesión iniciada
              </span>
            </label>

            {/* BUTTON */}
            <Button
              type="submit"
              disabled={submitting}
              className="group relative w-full h-12 rounded-xl text-white font-semibold text-[15px] overflow-hidden shadow-[0_10px_30px_rgba(184,26,151,0.30)] hover:shadow-[0_14px_40px_rgba(184,26,151,0.42)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.985] transition-all mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                background:
                  'linear-gradient(135deg, #B81A97 0%, #8E1374 100%)',
              }}
            >
              {/* Shine */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out"
              />
              <span className="relative flex items-center justify-center gap-2">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Entrando…</span>
                  </>
                ) : (
                  <>
                    <span>Iniciar sesión</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </span>
            </Button>

          </form>

          {/* Pie */}
          <div className="mt-12 flex items-center justify-between text-xs text-slate-400">
            <span>© {new Date().getFullYear()} Innomatic</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Sistema operativo
            </span>
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* DERECHA — PANEL DECORATIVO     */}
      {/* ============================== */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{
          background:
            'linear-gradient(135deg, #B81A97 0%, #8E1374 55%, #6B0E58 100%)',
        }}
      >
        {/* Escena 3D: máquina de salón (tragaperras) */}
        <Suspense fallback={null}>
          <SlotScene />
        </Suspense>

        {/* Viñeta sutil para dar profundidad al 3D */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(107,14,88,0.30) 0%, transparent 30%, transparent 72%, rgba(107,14,88,0.6) 100%)',
          }}
        />

        {/* Blob claro arriba-derecha */}
        <div
          className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-40"
          style={{
            background:
              'radial-gradient(circle at center, rgba(255,255,255,0.35) 0%, transparent 65%)',
          }}
        />

        {/* Blob oscuro abajo-izquierda */}
        <div
          className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-50"
          style={{
            background:
              'radial-gradient(circle at center, #D456BB 0%, transparent 60%)',
            filter: 'blur(40px)',
          }}
        />

        {/* Líneas geométricas sutiles */}
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
        >
          <line
            x1="0%"
            y1="20%"
            x2="100%"
            y2="80%"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
          />
          <line
            x1="0%"
            y1="60%"
            x2="100%"
            y2="20%"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="1"
          />
          <circle
            cx="80%"
            cy="20%"
            r="120"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
            fill="none"
          />
          <circle
            cx="15%"
            cy="80%"
            r="80"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
            fill="none"
          />
        </svg>

      </div>
    </div>
  )
}
